// M7.2 made the runtime catalogue swappable via setMobCatalogue, mirroring
// the items.ts pattern from M7.1. Server loads from /content/mobs.json when
// present; admin app publishes via WS and clients refresh in-flight.

import { z } from "zod";
import { AssetPathSchema } from "./items.js";

export type MobAiType =
  | "melee"
  | "meleeFast"
  | "meleeShield"
  | "rangedKite"
  | "meleeHeavy";

export interface LootEntry {
  templateId: string;
  // Independent roll per entry: probability (0..1) that this drop fires.
  chance: number;
  // For stackable items (potions, gold, …); inclusive range. 1 if omitted.
  stack?: { min: number; max: number };
}

export interface LootTable {
  // Entries rolled independently — each successful chance produces one drop.
  // Mutable for wire compatibility with the admin publish path; runtime
  // mutations should still go through the catalogue setters, not in-place.
  entries: LootEntry[];
  // Per-mob bias (0..1): probability that any given drop is rolled as RARE
  // (with 1–3 affixes) instead of common. Spec default ≈ 0.10; elites bumped.
  rareBias: number;
}

export interface MobTemplate {
  id: string;
  name: string;
  displayTag: string;
  displayColor: number;
  level: { min: number; max: number };
  hp: number;
  damage: { min: number; max: number };
  aiType: MobAiType;
  aggroRangeTiles: number;
  leashRadiusTiles: number;
  spawnRadiusTiles: number;
  moveSpeedTilesPerSec: number;
  attackRangeTiles: number;
  attackCooldownMs: number;
  respawnMs: number;
  xpReward: number;
  loot: LootTable;
  // Inclusive copper drop range. Always rolled on death (no chance gate); the
  // amount is credited directly to the killer (no ground bag for currency).
  copperDrop: { min: number; max: number };
  // M7.2 — optional sprite path served from /client/public/assets/.
  // Single frame for now; sprite-sheet / animation support comes later
  // when real Kenney mob packs land. Missing-file = silent fallback to the
  // displayTag + displayColor placeholder.
  spritePath?: string;
  // M8 — per-school resistances. Each value 0..0.75 (cap matches the
  // player-side SPELL_RESIST_CAP). Omitted entries = 0 (no resist). All
  // optional so the existing builtin mobs require no migration.
  resists?: {
    physical?: number;
    divine?: number;
    necromancy?: number;
    water?: number;
    fire?: number;
    air?: number;
    earth?: number;
  };
  // M17.2 — Stunning Blow for mobs (REFONTE-STATS §5): a melee hit has
  // `chance` to stun the player for `durationMs`, through the same per-target
  // diminishing-returns as player stuns. Omitted = mob never stuns. The
  // Crypt Brute of the sous-sol teaches the stun by inflicting it.
  stun?: { chance: number; durationMs: number };
}

export interface MobSpawnDef {
  templateId: string;
  x: number;
  y: number;
}

export const BUILTIN_MOB_TEMPLATES = {
  cave_rat: {
    id: "cave_rat",
    name: "Cave Rat",
    displayTag: "R",
    displayColor: 0x8a4a2b,
    level: { min: 1, max: 3 },
    hp: 30,
    damage: { min: 2, max: 4 },
    aiType: "melee",
    aggroRangeTiles: 5,
    leashRadiusTiles: 20,
    spawnRadiusTiles: 4,
    moveSpeedTilesPerSec: 4,
    attackRangeTiles: 1,
    attackCooldownMs: 1500,
    respawnMs: 60_000,
    xpReward: 10,
    loot: {
      rareBias: 0.05,
      entries: [
        { templateId: "rusty_dagger", chance: 0.15 },
        { templateId: "leather_belt", chance: 0.1 },
        { templateId: "minor_health_potion", chance: 0.25, stack: { min: 1, max: 2 } },
        { templateId: "bread", chance: 0.2, stack: { min: 1, max: 3 } },
      ],
    },
    copperDrop: { min: 5, max: 30 },
  },
  grey_wolf: {
    id: "grey_wolf",
    name: "Grey Wolf",
    displayTag: "W",
    displayColor: 0x6b6b78,
    level: { min: 5, max: 8 },
    hp: 80,
    damage: { min: 6, max: 10 },
    aiType: "meleeFast",
    aggroRangeTiles: 8,
    leashRadiusTiles: 20,
    spawnRadiusTiles: 5,
    moveSpeedTilesPerSec: 6,
    attackRangeTiles: 1,
    attackCooldownMs: 1200,
    respawnMs: 60_000,
    xpReward: 50,
    loot: {
      rareBias: 0.08,
      entries: [
        { templateId: "iron_sword", chance: 0.1 },
        { templateId: "leather_tunic", chance: 0.15 },
        { templateId: "leather_boots", chance: 0.15 },
        { templateId: "minor_health_potion", chance: 0.3, stack: { min: 1, max: 3 } },
      ],
    },
    copperDrop: { min: 30, max: 100 },
  },
  goblin_warrior: {
    id: "goblin_warrior",
    name: "Goblin Warrior",
    displayTag: "G",
    displayColor: 0x556b2f,
    level: { min: 10, max: 15 },
    hp: 200,
    damage: { min: 12, max: 18 },
    aiType: "meleeShield",
    aggroRangeTiles: 6,
    leashRadiusTiles: 20,
    spawnRadiusTiles: 4,
    moveSpeedTilesPerSec: 4,
    attackRangeTiles: 1,
    attackCooldownMs: 1800,
    respawnMs: 90_000,
    xpReward: 200,
    loot: {
      rareBias: 0.12,
      entries: [
        { templateId: "iron_mace", chance: 0.12 },
        { templateId: "wooden_shield", chance: 0.18 },
        { templateId: "iron_helm", chance: 0.1 },
        { templateId: "chain_mail", chance: 0.08 },
        { templateId: "iron_belt", chance: 0.15 },
        { templateId: "minor_health_potion", chance: 0.35, stack: { min: 1, max: 3 } },
      ],
    },
    copperDrop: { min: 100, max: 300 },
  },
  skeleton_mage: {
    id: "skeleton_mage",
    name: "Skeleton Mage",
    displayTag: "S",
    displayColor: 0xd9d6c2,
    level: { min: 20, max: 25 },
    hp: 250,
    damage: { min: 25, max: 35 },
    aiType: "rangedKite",
    aggroRangeTiles: 10,
    leashRadiusTiles: 20,
    spawnRadiusTiles: 4,
    moveSpeedTilesPerSec: 3,
    attackRangeTiles: 7,
    attackCooldownMs: 2200,
    respawnMs: 90_000,
    xpReward: 800,
    loot: {
      rareBias: 0.18,
      entries: [
        { templateId: "apprentice_staff", chance: 0.15 },
        { templateId: "silver_amulet", chance: 0.1 },
        { templateId: "silver_ring", chance: 0.12 },
        { templateId: "traveler_cloak", chance: 0.15 },
        { templateId: "minor_mana_potion", chance: 0.4, stack: { min: 1, max: 3 } },
      ],
    },
    copperDrop: { min: 300, max: 800 },
  },
  // Frost test walker — used only in the `frost_corridor` test zone for
  // verifying the Frost Shock slow mechanic. Aggros at long range so it
  // walks straight at the player across the corridor; moderate base speed
  // (4 t/s) so a 50% slow drops it to a clearly-visible 2 t/s. Unkillable
  // + harmless so the player can take their time observing the speed change.
  frost_test_walker: {
    id: "frost_test_walker",
    name: "Frost Walker",
    displayTag: "F",
    displayColor: 0x7fc8ff,
    level: { min: 1, max: 1 },
    hp: 999_999,
    damage: { min: 0, max: 0 },
    aiType: "melee",
    aggroRangeTiles: 30,
    leashRadiusTiles: 50,
    spawnRadiusTiles: 0,
    moveSpeedTilesPerSec: 4,
    attackRangeTiles: 1,
    attackCooldownMs: 99_999,
    respawnMs: 5_000,
    xpReward: 0,
    loot: { rareBias: 0, entries: [] },
    copperDrop: { min: 0, max: 0 },
  },
  // Training dummy — Eldoria-only utility mob for testing spells. Never
  // aggros (aggroRange 0), never patrols (spawnRadius 0), doesn't attack
  // (damage 0 + 99s cooldown), basically unkillable (HP 999_999). No loot,
  // no XP. Aiming for "infinite punching bag" UX so the player can drill
  // cast pipelines without ever fearing for their life.
  training_dummy: {
    id: "training_dummy",
    name: "Training Dummy",
    displayTag: "T",
    displayColor: 0xb08868,
    level: { min: 1, max: 1 },
    hp: 999_999,
    damage: { min: 0, max: 0 },
    aiType: "melee",
    aggroRangeTiles: 0,
    leashRadiusTiles: 1,
    spawnRadiusTiles: 0,
    moveSpeedTilesPerSec: 1,
    attackRangeTiles: 1,
    attackCooldownMs: 99_999,
    respawnMs: 5_000,
    xpReward: 0,
    loot: { rareBias: 0, entries: [] },
    copperDrop: { min: 0, max: 0 },
  },
  brown_bear: {
    id: "brown_bear",
    name: "Brown Bear",
    displayTag: "B",
    displayColor: 0x4a2b1a,
    level: { min: 30, max: 40 },
    hp: 800,
    damage: { min: 40, max: 60 },
    aiType: "meleeHeavy",
    aggroRangeTiles: 8,
    leashRadiusTiles: 20,
    spawnRadiusTiles: 5,
    moveSpeedTilesPerSec: 5,
    attackRangeTiles: 1,
    attackCooldownMs: 2500,
    respawnMs: 180_000,
    xpReward: 5000,
    loot: {
      rareBias: 0.35,
      entries: [
        { templateId: "steel_sword", chance: 0.18 },
        { templateId: "iron_battleaxe", chance: 0.14 },
        { templateId: "steel_warhammer", chance: 0.08 },
        { templateId: "plate_mail", chance: 0.1 },
        { templateId: "steel_helm", chance: 0.12 },
        { templateId: "iron_shield", chance: 0.18 },
        { templateId: "copper_bracer", chance: 0.2 },
        { templateId: "minor_health_potion", chance: 0.5, stack: { min: 2, max: 5 } },
      ],
    },
    copperDrop: { min: 2000, max: 8000 },
  },
} as const satisfies Record<string, MobTemplate>;

// Back-compat alias — tests + a few callers still iterate this. New code
// should call getMobCatalogue() or getMobTemplate(id).
export const MOB_TEMPLATES = BUILTIN_MOB_TEMPLATES;
export type MobTemplateId = keyof typeof BUILTIN_MOB_TEMPLATES;

// =============================================================================
// Runtime registry (M7.2)
// =============================================================================

const runtimeMobRegistry = new Map<string, MobTemplate>(
  Object.entries(BUILTIN_MOB_TEMPLATES),
);

export function getMobTemplate(id: string): MobTemplate | undefined {
  return runtimeMobRegistry.get(id);
}

export function getMobCatalogue(): MobTemplate[] {
  return Array.from(runtimeMobRegistry.values());
}

export function setMobCatalogue(mobs: readonly MobTemplate[]): void {
  runtimeMobRegistry.clear();
  for (const m of mobs) runtimeMobRegistry.set(m.id, m);
}

export function setMobTemplate(template: MobTemplate): boolean {
  const existed = runtimeMobRegistry.has(template.id);
  runtimeMobRegistry.set(template.id, template);
  return existed;
}

export function _resetMobCatalogueForTests(): void {
  runtimeMobRegistry.clear();
  for (const [id, tpl] of Object.entries(BUILTIN_MOB_TEMPLATES)) {
    runtimeMobRegistry.set(id, tpl);
  }
}

// =============================================================================
// Zod schemas (M7.2)
// =============================================================================

const MOB_ID_REGEX = /^[a-z][a-z0-9_]{2,40}$/;

const LootEntrySchema = z
  .object({
    templateId: z.string().min(1),
    chance: z.number().min(0).max(1),
    stack: z
      .object({
        min: z.number().int().min(1),
        max: z.number().int().min(1),
      })
      .strict()
      .optional(),
  })
  .strict();

const LootTableSchema = z
  .object({
    entries: z.array(LootEntrySchema).max(20),
    rareBias: z.number().min(0).max(1),
  })
  .strict();

const MobAiTypeSchema = z.enum([
  "melee",
  "meleeFast",
  "meleeShield",
  "rangedKite",
  "meleeHeavy",
]);

export const MobTemplateSchema = z
  .object({
    id: z.string().regex(MOB_ID_REGEX),
    name: z.string().min(1).max(60),
    // 1–3 chars for the placeholder rendering badge (R, W, Gob…)
    displayTag: z.string().min(1).max(3),
    // 24-bit color baked into the sprite tint.
    displayColor: z.number().int().min(0).max(0xffffff),
    level: z
      .object({
        min: z.number().int().min(1).max(100),
        max: z.number().int().min(1).max(100),
      })
      .strict(),
    hp: z.number().int().min(1).max(1_000_000),
    damage: z
      .object({
        min: z.number().int().min(0).max(10_000),
        max: z.number().int().min(0).max(10_000),
      })
      .strict(),
    aiType: MobAiTypeSchema,
    aggroRangeTiles: z.number().int().min(0).max(60),
    leashRadiusTiles: z.number().int().min(0).max(100),
    spawnRadiusTiles: z.number().int().min(0).max(50),
    moveSpeedTilesPerSec: z.number().min(0).max(20),
    attackRangeTiles: z.number().int().min(1).max(30),
    attackCooldownMs: z.number().int().min(100).max(120_000),
    respawnMs: z.number().int().min(1_000).max(24 * 60 * 60 * 1000),
    xpReward: z.number().int().min(0).max(1_000_000),
    loot: LootTableSchema,
    copperDrop: z
      .object({
        min: z.number().int().min(0).max(1_000_000),
        max: z.number().int().min(0).max(1_000_000),
      })
      .strict(),
    spritePath: AssetPathSchema.optional(),
    resists: z
      .object({
        physical: z.number().min(0).max(0.95).optional(),
        divine: z.number().min(0).max(0.95).optional(),
        necromancy: z.number().min(0).max(0.95).optional(),
        water: z.number().min(0).max(0.95).optional(),
        fire: z.number().min(0).max(0.95).optional(),
        air: z.number().min(0).max(0.95).optional(),
        earth: z.number().min(0).max(0.95).optional(),
      })
      .strict()
      .optional(),
    stun: z
      .object({
        chance: z.number().min(0).max(1),
        durationMs: z.number().int().min(0).max(10_000),
      })
      .strict()
      .optional(),
  })
  .strict();

export const MobCatalogueSchema = z.array(MobTemplateSchema);
